import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useMutation } from "@apollo/client";
import { Box } from "@mui/material";
import {
  MyHrLogin,
  MyHrLoginTwoFactorInfo,
  MyHrLoginTwoFactorLocked,
  MyHrLoginTwoFactorLoginEmail,
  MyHrLoginTwoFactorLoginTotp,
  MyHrLoginTwoFactorRegisterEmail,
  MyHrLoginTwoFactorSelectType,
  MyHrLoginTwoFactorTotpInfo,
  MyHrLoginTwoFactorTotpRegister,
  MyHrPublicLayoutData,
  MyHrTwoFactorWarningDialog,
} from "my-hr/components";
import { MY_HR_CLEAR_FAILED_CODE } from "my-hr/graphql";
import {
  useMyHrLoginFormSelectorState,
  useMyHrUserByEmployee,
} from "my-hr/hooks";
import { loginFromState } from "my-hr/states/login-from-state";
import { useOcCurrentUserState, useOcLoginState, useOcProperty } from "oc/hooks";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";
import { useOcTwoFactorEnabledState } from "oc/hooks/use-two-factor-enabled-state";
import { twoFaEnabledDateState } from "oc/states/two-fa-enabled-date-state";
import { twoFaVerificationPendingState } from "oc/states/two-fa-verification-pending-state";

type MyHRUserLoginPageProps = {
  onLogin: (props: any) => void; // Replace 'any' with a more specific type if available
};

export const MyHRUserLoginPage: React.FC<MyHRUserLoginPageProps> = ({
  onLogin,
}) => {
  const history = useHistory();
  const location = useLocation<{ from?: { pathname: string } }>();
  const isMobile = useOcIsMobile();

  const dateAt = useRecoilValue(twoFaEnabledDateState);
  const setTwoFaVerificationPending = useSetRecoilState(twoFaVerificationPendingState);

  const [showWarnigDialog, setShowWarningDialog] = useState(false);

  const { state: loginFormSelectorState, setState: setLoginFormSelectorState } =
    useMyHrLoginFormSelectorState();

  const [, setFrom] = useRecoilState(loginFromState);

  let { employeeCode } = useOcCurrentUserState();
  const { data: userData, refetch: refetchUserData } = useMyHrUserByEmployee({
    employee: employeeCode,
  });

  const { onLogout } = useOcLoginState();
  const { state: twoFactorEnabled } = useOcTwoFactorEnabledState();

  const { data } = useOcProperty({
    resource: "system",
    key: "loginType",
  });

  const isSSO = data && data === "sso" ? true : false;

  const [clearFailedCodeMutation] = useMutation(MY_HR_CLEAR_FAILED_CODE);

  const handleLogin = async () => {
    const { from = { pathname: "/" } } = location.state ?? {};

    const fallback = { pathname: "/" };
    let validFrom = fallback;

    if (from.pathname && from.pathname.length > 2) {
      validFrom = from;
    }

    history.replace(validFrom);

    setLoginFormSelectorState("login");
    setShowWarningDialog(false);
    setTwoFaVerificationPending(false); // 2FA flow complete
    const email = userData?.email;
    if (email) {
      clearFailedCodeMutation({
        variables: { email },
      });
    }
  };

  const handle2FASuccess = async () => {
    // Complete login after successful 2FA verification
    // The onLogin was already called in handlePreLogin, so just navigate
    await handleLogin();
  };

  const handlePreLogin = (props: any) => {
    let { from } = location.state || { from: { pathname: "/" } };
    setFrom(from);

    const currentUser = props?.currentUser;
    const userTwoFactorEnabled = currentUser?.twoFactorEnabled;
    const userTwoFactorType = currentUser?.twoFactorType;

    // ALWAYS call onLogin to set the accessToken in Recoil state
    // This is REQUIRED for the 2FA verification mutations to work
    // Backend auth.mixin.js will enforce 2FA verification via _check2FAVerification
    // - If session.twoFactorVerified === false → Only 2FA operations allowed
    // - If session.twoFactorVerified === true → All operations allowed
    onLogin(props);

    if (currentUser?.twoFactorFailedCode >= 3) {
      setTwoFaVerificationPending(true); // 2FA flow started
      setLoginFormSelectorState("two-factor-locked");
    } else if (twoFactorEnabled && !userTwoFactorEnabled && !isSSO) {
      setTwoFaVerificationPending(true); // 2FA flow started
      setLoginFormSelectorState("two-factor-info");
    } else if (
      twoFactorEnabled &&
      userTwoFactorEnabled &&
      userTwoFactorType === "TOTP" &&
      !isSSO
    ) {
      setTwoFaVerificationPending(true); // 2FA flow started
      setLoginFormSelectorState("login-two-factor-totp");
    } else if (
      twoFactorEnabled &&
      userTwoFactorEnabled &&
      userTwoFactorType === "EMAIL" &&
      !isSSO
    ) {
      setTwoFaVerificationPending(true); // 2FA flow started
      setLoginFormSelectorState("login-two-factor-email");
    } else {
      setTwoFaVerificationPending(false); // No 2FA required
      handleLogin();
    }
  };

  const handleCancel = async () => {
    let result;
    try {
      result = await refetchUserData();
    } catch (e) {
      console.error(e);
    }

    const email = result?.data?.userByEmployee?.email;
    const failedCode = result?.data?.userByEmployee?.twoFactorFailedCode;

    if (email && failedCode < 3) {
      clearFailedCodeMutation({
        variables: { email },
      });
    }

    if (
      loginFormSelectorState === "login-two-factor-totp" ||
      loginFormSelectorState === "login-two-factor-email" ||
      loginFormSelectorState === "two-factor-locked" ||
      failedCode >= 3
    ) {
      setLoginFormSelectorState("login");
      await onLogout();
      return;
    }

    const now = new Date().getTime();
    const validFrom = new Date(dateAt).getTime();
    if (validFrom >= now) {
      setShowWarningDialog(true);
    } else {
      setLoginFormSelectorState("login");
    }
  };

  const handleCloseWarningDialog = () => {
    const now = new Date().getTime();
    const validFrom = new Date(dateAt).getTime();
    if (validFrom > now) {
      handleLogin();
      setShowWarningDialog(false);
      setLoginFormSelectorState("login");
    } else {
      setLoginFormSelectorState("login");
    }
  };

  return (
    <Box
      id="page-container"
      width="100%"
      display="flex"
      flexDirection={isMobile ? "column" : "row"}
      justifyContent="space-between"
      height="100%"
    >
      <MyHrTwoFactorWarningDialog
        open={showWarnigDialog}
        onClose={handleCloseWarningDialog}
        dateAt={dateAt}
      />
      {!isMobile && <MyHrPublicLayoutData smallScreen={isMobile} />}
      <Box
        id="form-container"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        style={{ backgroundColor: "white" }}
        textAlign="center"
        width={isMobile ? "100%" : "400px"}
        height="100%"
      >
        <Box display="flex" flexDirection="row" justifyContent="center">
          {loginFormSelectorState === "login" && (
            <MyHrLogin onLogin={handlePreLogin} isSSO={isSSO} />
          )}
          {loginFormSelectorState === "two-factor-info" && (
            <MyHrLoginTwoFactorInfo
              onCancel={handleCancel}
              onForward={setLoginFormSelectorState}
              isMobile={isMobile}
            />
          )}
          {loginFormSelectorState === "two-factor-select-type" && (
            <MyHrLoginTwoFactorSelectType
              onCancel={handleCancel}
              onForward={setLoginFormSelectorState}
              isMobile={isMobile}
            />
          )}
          {loginFormSelectorState === "two-factor-email-register" && (
            <MyHrLoginTwoFactorRegisterEmail
              onCancel={handleCancel}
              onForward={handleLogin}
              isMobile={isMobile}
            />
          )}
          {loginFormSelectorState === "two-factor-totp-info" && (
            <MyHrLoginTwoFactorTotpInfo
              onCancel={handleCancel}
              onForward={setLoginFormSelectorState}
              isMobile={isMobile}
            />
          )}
          {loginFormSelectorState === "two-factor-totp-register" && (
            <MyHrLoginTwoFactorTotpRegister
              onCancel={handleCancel}
              onForward={handleLogin}
              isMobile={isMobile}
            />
          )}
          {loginFormSelectorState === "two-factor-locked" && (
            <MyHrLoginTwoFactorLocked
              onCancel={handleCancel}
              isMobile={isMobile}
            />
          )}
          {loginFormSelectorState === "login-two-factor-totp" && (
            <MyHrLoginTwoFactorLoginTotp
              onForward={handle2FASuccess}
              onCancel={handleCancel}
            />
          )}
          {loginFormSelectorState === "login-two-factor-email" && (
            <MyHrLoginTwoFactorLoginEmail
              onForward={handle2FASuccess}
              onCancel={handleCancel}
              userData={userData}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MyHRUserLoginPage;
